import type { TabLockRepository, TabLockRow } from '@/persistence/ports';
import type { Evse3Database } from '../db';

/**
 * CAS-style lock backed by a `tabLocks` row keyed by `[cpid, wsUrl]`. The
 * uniqueness comes from the primary key — `add()` throws on conflict, which
 * is exactly the "another tab already holds it" signal we need.
 */
export class TabLockRepositoryDexie implements TabLockRepository {
  constructor(private readonly db: Evse3Database) {}

  async tryAcquire(row: TabLockRow): Promise<boolean> {
    try {
      await this.db.transaction('rw', this.db.tabLocks, async () => {
        const existing = await this.db.tabLocks.get([row.cpid, row.wsUrl]);
        if (existing) throw new ConflictError();
        await this.db.tabLocks.add(row);
      });
      return true;
    } catch (err) {
      if (err instanceof ConflictError) return false;
      // Dexie's ConstraintError (duplicate primary key) wins the race too.
      if (
        typeof err === 'object' &&
        err !== null &&
        'name' in err &&
        (err as { name: string }).name === 'ConstraintError'
      ) {
        return false;
      }
      throw err;
    }
  }

  async heartbeat(cpid: string, wsUrl: string, tabId: string, at: number): Promise<boolean> {
    return this.db.transaction('rw', this.db.tabLocks, async () => {
      const row = await this.db.tabLocks.get([cpid, wsUrl]);
      if (!row || row.tabId !== tabId) return false;
      await this.db.tabLocks.put({ ...row, lastHeartbeat: at });
      return true;
    });
  }

  async release(cpid: string, wsUrl: string, tabId: string): Promise<boolean> {
    return this.db.transaction('rw', this.db.tabLocks, async () => {
      const row = await this.db.tabLocks.get([cpid, wsUrl]);
      if (!row || row.tabId !== tabId) return false;
      await this.db.tabLocks.delete([cpid, wsUrl]);
      return true;
    });
  }

  async reapStale(cutoff: number): Promise<readonly TabLockRow[]> {
    return this.db.transaction('rw', this.db.tabLocks, async () => {
      const stale = await this.db.tabLocks.where('lastHeartbeat').below(cutoff).toArray();
      for (const row of stale) {
        await this.db.tabLocks.delete([row.cpid, row.wsUrl]);
      }
      return stale;
    });
  }

  async get(cpid: string, wsUrl: string): Promise<TabLockRow | null> {
    const row = await this.db.tabLocks.get([cpid, wsUrl]);
    return row ?? null;
  }

  async setOcppConnected(
    cpid: string,
    wsUrl: string,
    tabId: string,
    connected: boolean,
  ): Promise<boolean> {
    return this.db.transaction('rw', this.db.tabLocks, async () => {
      const row = await this.db.tabLocks.get([cpid, wsUrl]);
      if (!row || row.tabId !== tabId) return false;
      await this.db.tabLocks.put({ ...row, ocppConnected: connected });
      return true;
    });
  }
}

class ConflictError extends Error {
  constructor() {
    super('tabLock conflict');
  }
}
